// 公開メアド登録ページ（ドライバーが共通URL `/?register=1` でスマホアクセス）。
// 通常アプリ（事務所用）とは別レンダリング。StoreProvider に依存しない。
//   - confirm クエリなし: 入力フォーム（氏名+メアド+営業所）
//   - confirm クエリあり: 確認リンクの確定処理（/?register=1&confirm=<token>）

import React, { useEffect, useState } from "react";
import "./styles/driver-approval.css";
import logo from "./assets/hsd-logo.png";
import { registerSubmit, registerConfirm } from "./lib/registerApi";

const PAGE_BG = "#eef0f4";
const FONT = "'Noto Sans JP', -apple-system, system-ui, sans-serif";

export function PublicRegister({ confirmToken }: { confirmToken: string | null }) {
  if (confirmToken) return <ConfirmView token={confirmToken} />;
  return <RegisterForm />;
}

function RegisterForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [branch, setBranch] = useState("");
  const [state, setState] = useState<"idle" | "submitting" | "sent" | "needsReview" | "error">("idle");
  const [reviewReason, setReviewReason] = useState<"candidate" | "unmatched" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setError("お名前とメールアドレスを入力してください");
      return;
    }
    setState("submitting");
    setError(null);
    const r = await registerSubmit({ name: name.trim(), email: email.trim(), branch: branch.trim() || undefined });
    if (r.ok && r.result === "sent") setState("sent");
    else if (r.ok && r.result === "needsReview") {
      setReviewReason(r.reason ?? null);
      setState("needsReview");
    }
    else {
      setState("error");
      setError(r.error || "送信に失敗しました。少し時間をおいて再度お試しください。");
    }
  };

  if (state === "sent") {
    return (
      <Done
        title="確認メールを送信しました"
        lines={[
          `${email} 宛に確認メールをお送りしました。`,
          "メール内のボタン「登録を完了する」を押すと登録完了です。",
          "届かない場合は迷惑メールフォルダもご確認ください。",
        ]}
      />
    );
  }
  if (state === "needsReview") {
    // この経路では確認メールを送っていない。成功風に見せると「送れたのにメールが来ない」と
    // 受け取られるため、メールが届かないことを最初に明示する（2026-07-27）。
    return (
      <Done
        title="事務局で確認します"
        tone="notice"
        lines={[
          reviewReason === "candidate"
            ? "同じお名前の方が複数いるため、この場では登録を完了できませんでした。"
            : "入力されたお名前がドライバー名簿と一致しませんでした。",
          "このあと確認メールは届きません。事務局で確認のうえ登録しますので、そのままお待ちください。",
          "お名前の表記（漢字・スペース）に心当たりがある場合は、入力し直してお試しいただくこともできます。",
        ]}
      />
    );
  }

  return (
    <Shell>
      <h1 style={{ fontSize: 18, margin: "10px 0 4px" }}>メールアドレスのご登録</h1>
      <p style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.7, margin: "0 0 16px" }}>
        毎月の支払通知をメールでお受け取りいただくため、お名前とメールアドレスをご登録ください。
      </p>
      <form onSubmit={submit}>
        <Field label="お名前（必須）">
          <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} placeholder="例: 山田 太郎" />
        </Field>
        <Field label="メールアドレス（必須）">
          <input
            style={inputStyle}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="例: taro@example.com"
          />
        </Field>
        <Field label="営業所（任意）">
          <input
            style={inputStyle}
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
            placeholder="同じ氏名の方がいる場合のみ（例: 周南）"
          />
        </Field>
        {error && <div style={{ color: "#dc2626", fontSize: 13, margin: "8px 0" }}>{error}</div>}
        <button type="submit" disabled={state === "submitting"} style={btnStyle(state === "submitting")}>
          {state === "submitting" ? "送信中..." : "確認メールを送る"}
        </button>
      </form>
      <p style={{ fontSize: 11, color: "#9ca3af", marginTop: 14, lineHeight: 1.7 }}>
        ※ ご入力のアドレスに確認メールが届きます。メールのボタンを押して初めて登録が完了します。
      </p>
    </Shell>
  );
}

function ConfirmView({ token }: { token: string }) {
  const [state, setState] = useState<"loading" | "done" | "already" | "error">("loading");
  const [driverName, setDriverName] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await registerConfirm(token);
      if (!alive) return;
      if (r.ok) {
        setDriverName(r.driverName || "");
        setState(r.alreadyDone ? "already" : "done");
      } else {
        setError(r.error || "確認に失敗しました");
        setState("error");
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  if (state === "loading") {
    return (
      <Shell>
        <div style={{ textAlign: "center", padding: 20, color: "#6b7280" }}>確認しています...</div>
      </Shell>
    );
  }
  if (state === "error") {
    return (
      <Done
        title="登録を完了できませんでした"
        tone="error"
        lines={[error || "リンクが無効です", "お手数ですが事務局までお問い合わせください。"]}
      />
    );
  }
  if (state === "already") {
    return <Done title="すでに登録済みです" lines={[`${driverName} 様のメールアドレスは登録済みです。`, "ありがとうございます。"]} />;
  }
  return (
    <Done
      title="登録が完了しました"
      lines={[`${driverName} 様、ありがとうございます。`, "今後の支払通知をこのメールアドレスにお送りします。"]}
    />
  );
}

// ===== 共通パーツ =====

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: "100vh", background: PAGE_BG, fontFamily: FONT, color: "#1b2536", padding: "24px 16px" }}>
      <div style={{ maxWidth: 460, margin: "0 auto" }}>
        <img src={logo} alt="HS-deliverys" style={{ height: 22, marginBottom: 14 }} />
        <div style={{ background: "#fff", borderRadius: 14, padding: "20px 18px", boxShadow: "0 1px 3px rgba(0,0,0,.08)" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <span style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 4 }}>{label}</span>
      {children}
    </label>
  );
}

function Done({ title, lines, tone }: { title: string; lines: string[]; tone?: "error" | "notice" }) {
  const icon = tone === "error" ? "⚠️" : tone === "notice" ? "📩" : "✅";
  return (
    <Shell>
      <div style={{ textAlign: "center", padding: "10px 0" }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>{icon}</div>
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 10 }}>{title}</div>
        {lines.map((l, i) => (
          <p key={i} style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.8, margin: "2px 0" }}>
            {l}
          </p>
        ))}
      </div>
    </Shell>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "11px 12px",
  fontSize: 15,
  border: "1px solid #d1d5db",
  borderRadius: 9,
  outline: "none",
  fontFamily: FONT,
};

function btnStyle(disabled: boolean): React.CSSProperties {
  return {
    width: "100%",
    padding: "13px 0",
    fontSize: 15,
    fontWeight: 700,
    color: "#fff",
    background: disabled ? "#93b4f7" : "#2563eb",
    border: "none",
    borderRadius: 10,
    cursor: disabled ? "default" : "pointer",
    marginTop: 4,
  };
}
